import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { api, formatApiError } from "../lib/api";
import { CATEGORY_LIST } from "../lib/i18n";
import { toast } from "sonner";
import { UploadCloud, FileIcon, X, ArrowUpRight } from "lucide-react";

export default function Upload() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const inputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!loading && !user) {
    nav("/login");
    return null;
  }

  const onPick = (f) => {
    if (!f) return;
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    const map = {
      ppt: "powerpoint", pptx: "powerpoint",
      doc: "word", docx: "word",
      xls: "excel", xlsx: "excel", csv: "excel",
      png: "images", jpg: "images", jpeg: "images", webp: "images", gif: "images",
      svg: "icons",
      mp4: "video", mov: "video", webm: "video", avi: "video",
    };
    setCategory(map[ext] || "other");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!file) {
      setErr("Please select a file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      fd.append("description", description);
      fd.append("category", category);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("uploadSuccess"));
      nav(`/asset/${data.id}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-72px)] py-14">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-fg">§ — New asset</div>
        <h1 className="font-display mt-3 text-5xl leading-[0.95] sm:text-6xl" data-testid="upload-title">
          <span className="font-serif text-accent">Share</span>{" "}
          <span className="text-ink">your work.</span>
        </h1>
        <p className="mt-4 max-w-lg text-base text-muted-fg">{t("uploadSub")}</p>

        <form onSubmit={submit} className="mt-10 space-y-6 card p-6 sm:p-8">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onPick(f);
            }}
            onClick={() => inputRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed py-12 px-6 text-center transition-colors`}
            style={{
              borderColor: drag ? "hsl(var(--accent))" : "hsl(var(--border))",
              backgroundColor: drag ? "hsl(var(--accent-soft))" : "hsl(var(--surface-2))",
            }}
            data-testid="upload-dropzone"
          >
            {file ? (
              <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: "hsl(var(--surface))", border: "1px solid hsl(var(--border))" }}>
                <FileIcon className="h-6 w-6 text-accent" />
                <div className="text-start">
                  <div className="text-sm font-semibold text-ink">{file.name}</div>
                  <div className="text-xs text-muted-fg">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="rounded-full p-1 text-muted-fg hover:bg-[hsl(var(--surface-2))]"
                  data-testid="upload-clear-file"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <UploadCloud className="h-12 w-12 text-muted-fg" />
                <div className="font-display mt-3 text-2xl text-ink">{t("selectFile")}</div>
                <div className="mt-1 text-xs text-muted-fg">PNG · JPG · PDF · DOCX · MP4 — up to 200MB</div>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
              data-testid="upload-file-input"
            />
          </div>

          <Field label={t("title")}>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input-base"
              data-testid="upload-title-input"
              required
            />
          </Field>

          <Field label={t("description")}>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-base"
              data-testid="upload-description"
            />
          </Field>

          <Field label={t("category")}>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="input-base"
              data-testid="upload-category"
            >
              {CATEGORY_LIST.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.id}
                </option>
              ))}
            </select>
          </Field>

          {err && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300" data-testid="upload-error">{err}</div>}

          <button type="submit" disabled={busy || !file} className="btn btn-accent w-full !py-3.5" data-testid="upload-submit">
            <UploadCloud className="h-4 w-4" />
            {busy ? t("uploading") : t("uploadBtn")}
            <ArrowUpRight className="h-4 w-4" />
          </button>
        </form>
      </div>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-fg">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
